import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays, setHours, setMinutes } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import { formatPrice } from '../../config/currencies';
import api from '../../services/api';
import AvailabilityCalendar from './AvailabilityCalendar';
import SlotPicker from './SlotPicker';
import RealTimePricing from './RealTimePricing';
import { calculateUnifiedPrice, formatUnifiedPrice } from '../../utils/priceCalculatorUnified';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import { formatDateSafe, debugDateFormatting } from '../../utils/dateFormatter';
import StripePaymentModal from '../payment/StripePaymentModal';
import CashConfirmationModal from '../payment/CashConfirmationModal';
import PaymentErrorModal from '../payment/PaymentErrorModal';
import BookingSettingsSummary from './BookingSettingsSummary';
import ReadOnlyMap from '../common/ReadOnlyMap';
import LoadingOverlay from '../common/LoadingOverlay';
import '../payment/StripePaymentModal.css';
import '../payment/PaymentErrorModal.css';

const ReservationSidebar = ({ listing, onClose, open = true }) => {
  const { t, i18n } = useTranslation('booking');

  const dateLocale = (i18n && i18n.language && i18n.language.startsWith('fr')) ? fr
    : (i18n && i18n.language && i18n.language.startsWith('ar')) ? ar
    : enUS;
  const { showNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fonction pour formater le prix avec la devise du listing UNIQUEMENT
  const formatListingPrice = (amount) => {
    if (!listing?.currency) {
      console.warn('[ReservationSidebar] No currency found in listing, using EUR as fallback');
      return formatPrice(amount, 'EUR');
    }
    return formatPrice(amount, listing.currency);
  };

  // États pour les dates sélectionnées
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const isTimeSlotMode = ['time_slot', 'time_slot_capacity'].includes(listing?.booking_mode);
  const isDateRangeMode = listing?.booking_mode === 'date_range';
  const isPropertyMode = listing?.offer_type === 'property' && isDateRangeMode;
  const defaultCheckinTime = isPropertyMode ? '14:00' : '09:00';
  const defaultCheckoutTime = isPropertyMode ? '12:00' : '18:00';

  // Construire un datetime wall-clock à partir d'une date et d'une heure "HH:MM"
  const buildPropertyDateTime = (date, timeStr) => {
    if (!date) return undefined;
    const [h, m] = (timeStr || '14:00').split(':').map(Number);
    const d = setMinutes(setHours(new Date(date), h), m);
    return format(d, "yyyy-MM-dd'T'HH:mm:ss");
  };

  // Mode nuitées vs journées pour date_range
  const isNightsMode = listing?.booking_settings?.date_range_mode === 'nights';

  // Calcul de la durée selon le mode : END-EXCLUSIVE pour les deux (nuitées & journées)
  // 22 -> 23 = 1 jour / 1 nuit
  const computeDateRangeDuration = useCallback((start, end) => {
    if (!start || !end) return 0;
    const diff = differenceInDays(end, start);
    return diff; // Exclusif pour les deux modes
  }, [isNightsMode]);

  // Label dynamique pour l'unité — utiliser les traductions centralisées dans `settings.dateRangeMode`
  const durationUnitLabel = isNightsMode
    ? t('settings.dateRangeMode.nights', 'nuitée(s)')
    : t('settings.dateRangeMode.days', 'jour(s)');

  // Le champ guest_count n'est utile que pour les modes à capacité partagée
  const hasCapacity = listing?.booking_mode === 'time_slot_capacity' && listing?.capacity_default > 1;
  const hasMultipleUnits = (listing?.stock_quantity || 1) > 1;

  // États créneaux – gérés directement par SlotPicker via callback
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [slotSelectionValid, setSlotSelectionValid] = useState(false);
  const [slotSelectionReason, setSlotSelectionReason] = useState(null);

  // Dédoublonnage : SlotPicker peut rappeler onSelectionChange avec les mêmes valeurs
  // (nouveaux objets Date) → évite de re-setter l'état en boucle.
  const lastSlotSelectionKeyRef = useRef(null);
  
  // États pour le calcul de prix local
  const [localPriceCalculation, setLocalPriceCalculation] = useState(null);
  
  // États pour le calcul de prix backend (lors de la réservation)
  const [priceCalculation, setPriceCalculation] = useState(null);
  // Ref miroir de priceCalculation pour éviter les closures stales dans calculatePrice
  const priceCalculationRef = useRef(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  // Ref pour éviter de relancer le même calcul qui a déjà échoué (boucle infinie)
  const lastFailedPriceKey = useRef(null);
  // Ref pour éviter de relancer le même calcul qui a déjà réussi
  const lastSucceededPriceKey = useRef(null);
  // Ref pour éviter plusieurs requêtes concurrentes identiques
  const inFlightPriceKey = useRef(null);
  
  // État pour les données d'affichage unifiées
  const [displayData, setDisplayData] = useState(null);
  
  // États pour le formulaire de réservation
  const [formData, setFormData] = useState({
    guest_count: 1,
    quantity_requested: 1,
    seats_requested: 1,
    special_requests: ''
  });

  // État du processus de réservation
  const [reservationStep, setReservationStep] = useState('calendar'); // 'calendar', 'form', 'payment', 'stripe_payment', 'confirmation'
  
  // État pour le choix du moyen de paiement
  const [paymentMethod, setPaymentMethod] = useState(null); // 'cash', 'bank_transfer', 'card'
  
  // États pour Stripe
  const [stripeData, setStripeData] = useState(null);
  const [reservationCreated, setReservationCreated] = useState(null);
  
  // États pour le modal de confirmation cash / virement
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashModalData, setCashModalData] = useState(null);

  // Erreur de disponibilité / stock retournée par calculate-amount (booking engine)
  const [priceError, setPriceError] = useState(null);

  // Nombre d'unités réellement disponibles sur les dates sélectionnées
  const [availableUnits, setAvailableUnits] = useState(listing?.stock_quantity || 1);

  // Nombre de places réellement disponibles sur le créneau sélectionné (mode time_slot_capacity)
  const [availableCapacity, setAvailableCapacity] = useState(listing?.capacity_default || null);

  // États pour la gestion d'erreur de paiement
  const [paymentError, setPaymentError] = useState(null);
  const [showPaymentError, setShowPaymentError] = useState(false);

  // Overlay de chargement pendant la soumission Stripe
  const [stripeProcessing, setStripeProcessing] = useState(false);

  // Callback reçu depuis SlotPicker – memoized pour éviter une boucle de re-render
  // (SlotPicker dépend de onSelectionChange dans un useEffect).
  const handleSlotSelectionChange = useCallback(({ selectedSlots: sel, startDate: s, endDate: e, valid, reason }) => {
    const selectionKey = sel?.length
      ? `${sel.length}|${sel[0]?.start_at}|${sel[sel.length - 1]?.end_at}|${valid ? '1' : '0'}|${reason || ''}`
      : `empty|${valid ? '1' : '0'}|${reason || ''}`;
    if (lastSlotSelectionKeyRef.current === selectionKey) {
      return;
    }
    lastSlotSelectionKeyRef.current = selectionKey;

    setSelectedSlots(sel);
    setSlotSelectionValid(valid);
    setSlotSelectionReason(reason);
    // SlotPicker passe des strings wall-clock sans Z (ex: "2026-02-22T19:00:00").
    // new Date(str_sans_Z) est interprété en heure locale par le navigateur → 19h = 19h ✅
    setStartDate(s ? new Date(s) : null);
    setEndDate(e ? new Date(e) : null);
    setPriceCalculation(null);
    priceCalculationRef.current = null;
    setPriceError(null);
    // Réinitialiser la clé d'échec quand la sélection change
    lastFailedPriceKey.current = null;
    lastSucceededPriceKey.current = null;
    inFlightPriceKey.current = null;
  }, []);

  // Calcul automatique du prix lors du changement de dates
  useEffect(() => {
    if (startDate && endDate && listing && !isTimeSlotMode) {
      console.log('💰 [ReservationSidebar] Calculating local price for dates:', {
        start: format(startDate, 'yyyy-MM-dd'),
        end: format(endDate, 'yyyy-MM-dd'),
        listing: listing.id
      });

      // calculateUnifiedPrice est async — récupère les taux depuis la BDD
      calculateUnifiedPrice(listing, startDate, endDate, {}).then(calculation => {
        if (!calculation) return;
        console.log('💰 [ReservationSidebar] Calcul unifié COMPLET:', calculation);
        console.log('💰 [ReservationSidebar] Raw data:', calculation.raw);
        console.log('💰 [ReservationSidebar] Breakdown data:', calculation.breakdown);
        setLocalPriceCalculation(calculation);
        if (priceCalculation) {
          setDisplayData(getDisplayData(priceCalculation, calculation));
        }
      }).catch(err => {
        console.error('❌ [ReservationSidebar] Local calculation failed:', err);
      });

      // Déclencher aussi le calcul backend automatiquement
      calculatePrice(startDate, endDate);
    } else if (startDate && endDate && listing && isTimeSlotMode) {
      // Slot mode: rely on backend calculation (which uses booking-engine validate)
      setLocalPriceCalculation(null);
      if (slotSelectionValid) {
        calculatePrice(startDate, endDate);
      }
    } else {
      setLocalPriceCalculation(null);
    }
  }, [startDate, endDate, listing?.id, isTimeSlotMode, slotSelectionValid, formData.quantity_requested, formData.seats_requested, formData.guest_count]);

  // ✅ Fetch remaining units quand les dates changent (pour les listings avec stock > 1)
  useEffect(() => {
    if (!startDate || !endDate || !listing?.id || !open) return;
    if (listing.stock_quantity <= 1) {
      setAvailableUnits(listing.stock_quantity || 1);
      return;
    }

    const fetchAvailableUnits = async () => {
      try {
        const queryParams = {
          listing_id: listing.id,
          quantity_requested: 1 // on veut juste remaining_units
        };
        // Pour time_slot : utiliser les ISO datetimes ; pour date_range : utiliser les dates
        if (isTimeSlotMode && slotSelectionValid) {
          queryParams.start_date = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
          queryParams.end_date = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
        } else if (!isTimeSlotMode) {
          queryParams.start_date = format(startDate, 'yyyy-MM-dd');
          queryParams.end_date = format(endDate, 'yyyy-MM-dd');
        } else {
          return; // time_slot sans sélection valide — ne pas fetch
        }
        const response = await api.get('/reservations/availability', { params: queryParams });
        const remaining = response.data?.data?.remaining_units;
        if (remaining !== undefined && remaining !== null) {
          const safeRemaining = Math.max(0, remaining);
          setAvailableUnits(safeRemaining);
          // Si la quantité demandée dépasse le stock dispo, réduire automatiquement
          if (formData.quantity_requested > safeRemaining && safeRemaining > 0) {
            handleFormChange('quantity_requested', safeRemaining);
          }
        }
      } catch (err) {
        console.warn('⚠️ [ReservationSidebar] Could not fetch remaining units:', err.message);
        // Fallback au stock brut
        setAvailableUnits(listing.stock_quantity || 1);
      }
    };
    fetchAvailableUnits();
  }, [startDate, endDate, listing?.id, open, isTimeSlotMode, slotSelectionValid]);

  // ✅ Fetch remaining capacity quand le créneau change (pour les listings time_slot_capacity)
  useEffect(() => {
    if (!startDate || !endDate || !listing?.id || !open) return;
    if (listing.booking_mode !== 'time_slot_capacity' || !listing.capacity_default) {
      setAvailableCapacity(null);
      return;
    }
    if (!slotSelectionValid) return;

    const fetchAvailableCapacity = async () => {
      try {
        const response = await api.get('/reservations/availability', {
          params: {
            listing_id: listing.id,
            start_date: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
            end_date: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
            seats_requested: 1 // on veut juste remaining_capacity
          }
        });
        const remainingCap = response.data?.data?.remaining_capacity;
        if (remainingCap !== undefined && remainingCap !== null) {
          const safeCap = Math.max(0, remainingCap);
          setAvailableCapacity(safeCap);
          // Réduire automatiquement seats_requested et guest_count si dépassent la capacité dispo
          if (formData.seats_requested > safeCap && safeCap > 0) {
            handleFormChange('seats_requested', safeCap);
          }
          if (formData.guest_count > safeCap && safeCap > 0) {
            handleFormChange('guest_count', safeCap);
          }
        } else {
          // Pas de mode capacité dans la réponse — fallback
          setAvailableCapacity(listing.capacity_default);
        }
      } catch (err) {
        console.warn('⚠️ [ReservationSidebar] Could not fetch remaining capacity:', err.message);
        setAvailableCapacity(listing.capacity_default);
      }
    };
    fetchAvailableCapacity();
  }, [startDate, endDate, listing?.id, open, slotSelectionValid]);

  // Mettre à jour displayData quand les calculs changent
  useEffect(() => {
    setDisplayData(getDisplayData());
  }, [priceCalculation, localPriceCalculation, startDate, endDate]);

  // Gérer les changements de dates
  const handleDateChange = (start, end) => {
    console.log('📅 [ReservationSidebar] Date change received:', {
      start: start ? format(start, 'yyyy-MM-dd') : 'null',
      end: end ? format(end, 'yyyy-MM-dd') : 'null',
      startType: typeof start,
      endType: typeof end
    });
    
    setStartDate(start);
    setEndDate(end);
    setPriceCalculation(null); // Reset backend price when dates change
    priceCalculationRef.current = null;
    setPriceError(null);
    lastFailedPriceKey.current = null;
    lastSucceededPriceKey.current = null;
    inFlightPriceKey.current = null;
    lastSlotSelectionKeyRef.current = null;
    setReservationStep('calendar');
  };

  // Calculer le prix de la réservation (backend - pour la réservation finale)
  const calculatePrice = async (start, end) => {
    if (!start || !end || !listing?.id || !open) return;

    let requestKey = null;

    try {
      console.log('💰 [ReservationSidebar] Calculating price...');
      
      const params = {
        listing_id: listing.id,
        // En mode time_slot, on envoie l'heure wall-clock sans Z pour éviter toute conversion UTC
        start_date: isTimeSlotMode ? format(start, "yyyy-MM-dd'T'HH:mm:ss") : format(start, 'yyyy-MM-dd'),
        end_date: isTimeSlotMode ? format(end, "yyyy-MM-dd'T'HH:mm:ss") : format(end, 'yyyy-MM-dd')
      };

      // Champs optionnels : ne pas envoyer de valeurs vides (sinon validation express-validator en 400)
      const guestCount = parseInt(formData.guest_count, 10);
      if (!Number.isNaN(guestCount) && guestCount >= 1) {
        params.guest_count = guestCount;
      }

      const qty = parseInt(formData.quantity_requested, 10);
      if (!Number.isNaN(qty) && qty >= 1) {
        params.quantity_requested = qty;
      }

      // In capacity pricing, seats_requested must remain the TOTAL number of seats/passengers.
      // Do NOT convert it to "seats per unit" with a ceil(), otherwise pricing becomes tiered (e.g. 7=8).
      const totalSeats = parseInt(formData.seats_requested, 10);
      if (!Number.isNaN(totalSeats) && totalSeats >= 1) {
        params.seats_requested = totalSeats;
      }

      // Déduplication (échec/succès/en cours) sur les paramètres réellement envoyés
      requestKey = JSON.stringify(params);
      if (lastFailedPriceKey.current === requestKey) return;
      // Utiliser la ref (pas l'état) pour éviter les closures stales
      if (lastSucceededPriceKey.current === requestKey && priceCalculationRef.current) return;
      if (inFlightPriceKey.current === requestKey) return;
      inFlightPriceKey.current = requestKey;

      // Set loading AFTER dedup guards – prevents stuck loading state on deduped calls
      setCalculatingPrice(true);

      const response = await api.get('/reservations/calculate-amount', { params });
      
      console.log('� [ReservationSidebar] Price calculation:', response.data);
      const backendData = response.data.data;
      setPriceCalculation(backendData);
      priceCalculationRef.current = backendData;
      lastSucceededPriceKey.current = requestKey;
      lastFailedPriceKey.current = null;
      
      // Mettre à jour l'affichage unifié avec les données du backend
      if (localPriceCalculation) {
        setDisplayData(getDisplayData(backendData, localPriceCalculation));
      }
      
    } catch (error) {
      console.error('❌ [ReservationSidebar] Error calculating price:', error);
      const apiMessage = error?.response?.data?.message;
      const apiCode = error?.response?.data?.error_code;
      // Mémoriser la clé de requête ayant échoué pour éviter une boucle infinie
      if (requestKey) lastFailedPriceKey.current = requestKey;

      // Afficher les erreurs de disponibilité / stock à l'utilisateur dans l'UI
      // (pas via showNotification pour éviter la boucle infinie).
      if (apiMessage) {
        setPriceError(apiMessage);
        setPriceCalculation(null);
        priceCalculationRef.current = null;
      }
      console.warn('💥 [ReservationSidebar] Price calculation failed:', apiMessage || error.message, apiCode || '');
    } finally {
      if (inFlightPriceKey.current === requestKey) {
        inFlightPriceKey.current = null;
      }
      setCalculatingPrice(false);
    }
  };

  // Gérer les changements du formulaire
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      // In time_slot_capacity, seats_requested is the canonical "capacity" axis.
      // Keep guest_count aligned to avoid divergent values and UI confusion.
      if (hasCapacity && (field === 'seats_requested' || field === 'guest_count')) {
        const v = value;
        return {
          ...prev,
          seats_requested: v,
          guest_count: v,
        };
      }

      return {
        ...prev,
        [field]: value
      };
    });
    
    // Pricing recalculation is handled by the useEffect that watches
    // formData.quantity_requested / seats_requested / guest_count.
    // Invalidate dedup keys so the next calculatePrice actually fires.
    if (['guest_count', 'quantity_requested', 'seats_requested'].includes(field)) {
      lastSucceededPriceKey.current = null;
      lastFailedPriceKey.current = null;
      // Clear any existing price/availability error when user adjusts quantities
      setPriceError(null);
    }
  };

  // Vérifier la disponibilité
  const checkAvailability = async (start, end) => {
    // Vérifier que les dates sont des objets Date valides
    if (!start || !end || !(start instanceof Date) || !(end instanceof Date)) {
      console.error('❌ [ReservationSidebar] Invalid dates for availability check:', { start, end });
      return { success: false, message: 'Dates invalides' };
    }

    console.log('🔍 [ReservationSidebar] Checking availability for:', {
      listingId: listing?.id,
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      startType: typeof start,
      endType: typeof end
    });
    
    try {
      const response = await api.get('/reservations/availability', {
        params: {
          listing_id: listing.id,
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd'),
          quantity_requested: parseInt(formData.quantity_requested, 10) || 1
        }
      });
      
      console.log('📊 [ReservationSidebar] Availability response:', response);
      console.log('📊 [ReservationSidebar] Response data:', response.data);
      
      return {
        success: true,
        available: response.data.data?.available || false,
        remaining_units: response.data.data?.remaining_units,
        data: response.data
      };
    } catch (error) {
      console.error('❌ [ReservationSidebar] Error checking availability:', error);
      console.error('❌ [ReservationSidebar] Error response:', error.response);
      return {
        success: false,
        message: t('sidebar.notifications.availabilityError'),
        error: error
      };
    }
  };

  // Gérer le processus complet de réservation
  const handleReservation = async () => {
    console.log('🚀 [ReservationSidebar] Starting reservation process...');
    
    // Validation des dates
    if (!startDate || !endDate) {
      showNotification(t('sidebar.notifications.selectDates'), 'warning');
      return;
    }

    if (!listing?.id) {
      showNotification(t('sidebar.notifications.listingNotFound'), 'error');
      return;
    }

    // Si paiement par carte, créer d'abord la réservation avec Stripe
    if (paymentMethod === 'card') {
      await handleStripeReservation();
      return;
    }

    // Pour cash et bank_transfer, processus classique
    setLoading(true);
    
    try {
      // Étape 1: Vérifier la disponibilité
      // - date_range: legacy availability endpoint
      // - time_slot(_capacity): availability is enforced by booking-engine during pricing + reservation creation
      if (!isTimeSlotMode) {
        console.log('📋 [ReservationSidebar] Step 1: Checking availability...');
        const availabilityResult = await checkAvailability(startDate, endDate);

        if (!availabilityResult.success) {
          showNotification(availabilityResult.message || t('sidebar.notifications.availabilityError'), 'error');
          return;
        }

        if (!availabilityResult.available) {
          showNotification(t('sidebar.notifications.notAvailable'), 'warning');
          return;
        }
      }

      // Étape 2: Validation du formulaire
      console.log('📝 [ReservationSidebar] Step 2: Validating form...');

      if (!priceCalculation && !localPriceCalculation) {
        showNotification(t('sidebar.notifications.calculating'), 'info');
        return;
      }

      // Utiliser priceCalculation (backend) en priorité seulement si les valeurs sont valides
      const isBackendPricingValid = priceCalculation && 
                                   priceCalculation.total_amount !== null && 
                                   priceCalculation.total_amount !== undefined &&
                                   priceCalculation.total_amount > 0;

      const finalPriceCalculation = isBackendPricingValid ? priceCalculation : {
        total_amount: localPriceCalculation?.breakdown?.total_amount || localPriceCalculation?.raw?.client_total || 0,
        deposit_amount: localPriceCalculation?.breakdown?.deposit_amount || localPriceCalculation?.raw?.deposit_amount || 0
      };

      // Étape 3: Créer la réservation
      console.log('🎯 [ReservationSidebar] Step 3: Creating reservation...');
      
      // DEBUG: Logger les dates avant formatage
      console.log('🐛 DEBUG ReservationSidebar dates avant formatage:', {
        startDate,
        endDate,
        startDateWallClock: startDate instanceof Date ? format(startDate, "yyyy-MM-dd'T'HH:mm:ss") : startDate,
        endDateWallClock: endDate instanceof Date ? format(endDate, "yyyy-MM-dd'T'HH:mm:ss") : endDate
      });
      
      const startDateFormatted = formatDateSafe(startDate);
      const endDateFormatted = formatDateSafe(endDate);
      
      debugDateFormatting('ReservationSidebar - Dates après formatage', {
        startDateFormatted,
        endDateFormatted
      });
      
      const reservationData = {
        listing_id: listing.id,
        start_date: isTimeSlotMode ? (priceCalculation?.start_date || startDateFormatted) : startDateFormatted,
        end_date: isTimeSlotMode ? (priceCalculation?.end_date || endDateFormatted) : endDateFormatted,
        start_at: isTimeSlotMode
          ? (priceCalculation?.start_at || selectedSlots[0]?.start_at || format(startDate, "yyyy-MM-dd'T'HH:mm:ss"))
          : isDateRangeMode
            ? buildPropertyDateTime(startDate, listing?.booking_settings?.checkin_time || defaultCheckinTime)
            : undefined,
        end_at: isTimeSlotMode
          ? (priceCalculation?.end_at || selectedSlots[selectedSlots.length - 1]?.end_at || format(endDate, "yyyy-MM-dd'T'HH:mm:ss"))
          : isDateRangeMode
            ? buildPropertyDateTime(endDate, listing?.booking_settings?.checkout_time || defaultCheckoutTime)
            : undefined,
        pickup_address: listing?.address || '',
        return_address: listing?.address || '',
        total_price: finalPriceCalculation.total_amount,
        deposit_amount: finalPriceCalculation.deposit_amount,
        guest_count: hasCapacity ? formData.guest_count : 1,
        // Défense en profondeur : n'envoyer que les axes pertinents selon pricing_mode
        quantity_requested: ['per_unit', 'per_unit_per_seat'].includes(listing?.booking_settings?.pricing_mode)
          ? formData.quantity_requested : 1,
        // Keep seats_requested as TOTAL passengers (no ceil/tiers)
        seats_requested: (() => {
          const _pm = listing?.booking_settings?.pricing_mode;
          if (!['per_seat', 'per_unit_per_seat'].includes(_pm) || listing?.booking_mode !== 'time_slot_capacity') return null;
          return formData.seats_requested;
        })(),
        special_requests: formData.special_requests.trim(),
        payment_method: paymentMethod
      };

      console.log('📋 [ReservationSidebar] Reservation data with payment method:', reservationData);

      // Afficher le modal de confirmation cash au lieu d'appeler l'API directement
      const cashBreakdown = {
        rental_amount: displayData?.rental_amount || 0,
        service_fee: displayData?.service_fee || 0,
        tax_amount: displayData?.tax_amount || 0,
        total_amount: finalPriceCalculation.total_amount,
        deposit_amount: finalPriceCalculation.deposit_amount,
        days: displayData?.days || computeDateRangeDuration(startDate, endDate),
        currency: priceCalculation?.currency || listing?.currency || 'EUR',
        commission_rate_percent: displayData?.commission_rate_percent ?? null,
        vat_rate_percent: displayData?.vat_rate_percent ?? null,
      };
      setCashModalData({ reservationData, breakdown: cashBreakdown });
      setShowCashModal(true);
      return; // finally setLoading(false) s'exécutera quand même
      
    } catch (error) {
      console.error('❌ [ReservationSidebar] Reservation process failed:', error);
      
      let errorMessage = t('sidebar.notifications.creationError');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.map(e => e.msg).join(', ');
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Confirmer la réservation cash / virement après validation dans le modal
  const handleCashConfirmed = async () => {
    if (!cashModalData?.reservationData) return;
    setShowCashModal(false);
    setLoading(true);
    try {
      const response = await api.post('/reservations', cashModalData.reservationData);
      console.log('✅ [ReservationSidebar] Reservation created successfully:', response.data);
      showNotification(t('sidebar.notifications.success'), 'success');
      const reservationId = response.data.data?.reservation?.id ||
                            response.data.reservation?.id ||
                            response.data.data?.id ||
                            response.data.id ||
                            response.data.reservation_id;
      if (reservationId) {
        onClose();
        navigate(`/booking/confirmation/${reservationId}`);
      } else {
        console.error('❌ [ReservationSidebar] No reservation ID found in response');
        showNotification(t('sidebar.notifications.reservationIdMissing'), 'error');
        setReservationStep('confirmation');
        setReservationCreated(response.data);
      }
    } catch (error) {
      console.error('❌ [ReservationSidebar] Cash reservation failed:', error);
      let errorMessage = t('sidebar.notifications.creationError');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.map(e => e.msg).join(', ');
      }
      showNotification(errorMessage, 'error');
      setShowCashModal(true); // ré-ouvrir le modal pour que l'utilisateur puisse réessayer
    } finally {
      setLoading(false);
    }
  };

  // Gérer le processus de réservation avec Stripe - INTÉGRATION COMPLÈTE
  const handleStripeReservation = async () => {
    
    setLoading(true);
    
    try {
      // Calculer le prix final avec l'utilitaire unifié
      let finalPriceCalculation;
      
      // ✅ ALIGNÉ AVEC CASH FLOW : même validation stricte
      const isBackendPricingValid = priceCalculation && 
                                   priceCalculation.total_amount !== null && 
                                   priceCalculation.total_amount !== undefined &&
                                   priceCalculation.total_amount > 0;
      
      if (isBackendPricingValid) {
        // Utiliser les données backend si disponibles
        finalPriceCalculation = {
          rental_amount: priceCalculation.rental_amount || priceCalculation.base_amount,
          deposit_amount: priceCalculation.deposit_amount,
          service_fee: priceCalculation.service_fee,
          tax_amount: priceCalculation.tax_amount || 0,
          total_amount: priceCalculation.total_amount,
          currency: priceCalculation.currency || listing.currency,
          days: computeDateRangeDuration(startDate, endDate),
          commission_rate_percent: priceCalculation.commission_rate != null ? parseFloat((priceCalculation.commission_rate * 100).toFixed(2)) : null,
          vat_rate_percent: priceCalculation.vat_rate != null ? parseFloat((priceCalculation.vat_rate * 100).toFixed(2)) : null
        };
      } else if (localPriceCalculation && localPriceCalculation.isValid) {
        // Utiliser le calcul unifié local (enrichi avec les taux BDD)
        finalPriceCalculation = {
          ...localPriceCalculation.breakdown,
          commission_rate_percent: localPriceCalculation.raw?.commission_rate_used ?? null,
          vat_rate_percent: localPriceCalculation.raw?.vat_rate_used ?? null
        };
      } else {
        // Recalculer avec l'utilitaire unifié (async — taux depuis la BDD)
        const freshCalculation = await calculateUnifiedPrice(listing, startDate, endDate, {});
        
        if (!freshCalculation?.isValid) {
          showNotification(`${t('sidebar.notifications.calculationError')}: ${freshCalculation?.error || 'Unknown error'}`, 'error');
          setLoading(false);
          return;
        }
        
        finalPriceCalculation = {
          ...freshCalculation.breakdown,
          commission_rate_percent: freshCalculation.raw?.commission_rate_used ?? null,
          vat_rate_percent: freshCalculation.raw?.vat_rate_used ?? null
        };
      }

      // FIX: Le backend n'inclut PAS le dépôt dans total_amount, mais le local (priceCalculatorUnified) l'inclut DÉJÀ.
      // On doit ajouter le dépôt UNIQUEMENT quand la source est le backend.
      // On crée un nouvel objet pour ne PAS muter l'état React.
      const depositAmount = finalPriceCalculation.deposit_amount || 0;
      const totalForStripe = isBackendPricingValid
        ? finalPriceCalculation.total_amount + depositAmount   // backend: dépôt NON inclus → ajouter
        : finalPriceCalculation.total_amount;                  // local: dépôt DÉJÀ inclus → ne pas re-ajouter

      // Créer un nouvel objet (pas de mutation de l'état React)
      finalPriceCalculation = {
        ...finalPriceCalculation,
        total_amount: totalForStripe,
        // Ensure tax_amount is always present for display in Stripe modal
        tax_amount: finalPriceCalculation.tax_amount ??
          Math.round((finalPriceCalculation.service_fee || 0) * (finalPriceCalculation.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT) / 100 * 100) / 100
      };

      console.log('💳 [ReservationSidebar] Total pour Stripe:', {
        'Source': isBackendPricingValid ? 'BACKEND' : 'UNIFIED_LOCAL',
        'Rental': finalPriceCalculation.rental_amount,
        'Deposit': depositAmount,
        'Service Fee': finalPriceCalculation.service_fee,
        'Total final pour paiement': totalForStripe
      });

      if (finalPriceCalculation.total_amount <= 0) {
        showNotification(t('sidebar.notifications.calculationError'), 'error');
        setLoading(false);
        return;
      }

      // Préparer les données pour le modal de paiement
      // ✅ ALIGNÉ AVEC CASH FLOW : même format de dates
      const startDateFormatted = formatDateSafe(startDate);
      const endDateFormatted = formatDateSafe(endDate);

      const stripePaymentData = {
        reservationData: {
          listing_id: listing.id,
          start_date: isTimeSlotMode ? (priceCalculation?.start_date || startDateFormatted) : startDateFormatted,
          end_date: isTimeSlotMode ? (priceCalculation?.end_date || endDateFormatted) : endDateFormatted,
          start_at: isTimeSlotMode
            ? (priceCalculation?.start_at || selectedSlots[0]?.start_at || format(startDate, "yyyy-MM-dd'T'HH:mm:ss"))
            : isDateRangeMode
              ? buildPropertyDateTime(startDate, listing?.booking_settings?.checkin_time || defaultCheckinTime)
              : undefined,
          end_at: isTimeSlotMode
            ? (priceCalculation?.end_at || selectedSlots[selectedSlots.length - 1]?.end_at || format(endDate, "yyyy-MM-dd'T'HH:mm:ss"))
            : isDateRangeMode
              ? buildPropertyDateTime(endDate, listing?.booking_settings?.checkout_time || defaultCheckoutTime)
              : undefined,
          pickup_address: listing?.address || '',
          return_address: listing?.address || '',
          total_price: finalPriceCalculation.total_amount,
          deposit_amount: finalPriceCalculation.deposit_amount,
          guest_count: hasCapacity ? formData.guest_count : null,
          // Défense en profondeur : n'envoyer que les axes pertinents selon pricing_mode
          quantity_requested: ['per_unit', 'per_unit_per_seat'].includes(listing?.booking_settings?.pricing_mode)
            ? formData.quantity_requested : 1,
          // Keep seats_requested as TOTAL passengers (no ceil/tiers)
          seats_requested: (() => {
            const _pm = listing?.booking_settings?.pricing_mode;
            if (!['per_seat', 'per_unit_per_seat'].includes(_pm) || listing?.booking_mode !== 'time_slot_capacity') return null;
            return formData.seats_requested;
          })(),
          special_requests: formData.special_requests.trim(),
          payment_method: 'card',
          renterName: formData.renterName || 'Client Krillou',
          renterEmail: formData.renterEmail
        },
        breakdown: finalPriceCalculation
      };

      console.log('🎯 [ReservationSidebar] Opening Stripe payment modal with data:', {
        reservationData: stripePaymentData.reservationData,
        breakdown: finalPriceCalculation
      });

      // Mettre à jour les données ET changer l'étape en même temps
      setStripeData(stripePaymentData);
      setReservationStep('stripe_payment');
      
    } catch (error) {
      console.error('❌ [ReservationSidebar] Error preparing Stripe payment:', error);
      showNotification(t('sidebar.notifications.paymentPreparationError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les données d'affichage unifiées pour le sidebar
  const getDisplayData = (backendData = null, localData = null) => {
    // Utiliser les données passées en paramètres ou les états actuels
    const priceCalc = backendData || priceCalculation;
    const localCalc = localData || localPriceCalculation;
    
    // Priorité : backend validé > calcul local > fallback
    const isBackendValid = priceCalc && priceCalc.total_amount > 0;

    // Extraire les taux réels depuis la BDD (source de vérité unique)
    const commissionRatePercent = priceCalc?.commission_rate != null
      ? parseFloat((priceCalc.commission_rate * 100).toFixed(2))
      : (localCalc?.isValid ? localCalc.raw?.commission_rate_used ?? null : null);
    const vatRatePercent = priceCalc?.vat_rate != null
      ? parseFloat((priceCalc.vat_rate * 100).toFixed(2))
      : (localCalc?.isValid ? localCalc.raw?.vat_rate_used ?? null : null);
    
    if (isBackendValid) {
      return {
        source: 'backend',
        rental_amount: priceCalc.rental_amount || priceCalc.base_amount,
        service_fee: priceCalc.service_fee,
        tax_amount: priceCalc.tax_amount,
        total_amount: priceCalc.total_amount,
        deposit_amount: priceCalc.deposit_amount,
        days: computeDateRangeDuration(startDate, endDate),
        daily_rate: priceCalc.daily_rate,
        number_of_days: priceCalc.number_of_days,
        commission_rate_percent: commissionRatePercent,
        vat_rate_percent: vatRatePercent
      };
    } else if (localCalc && localCalc.isValid) {
      return {
        source: 'unified',
        rental_amount: localCalc.breakdown.rental_amount,
        service_fee: localCalc.breakdown.service_fee,
        tax_amount: localCalc.raw.tax_amount || 0,
        total_amount: localCalc.breakdown.total_amount,
        deposit_amount: localCalc.breakdown.deposit_amount,
        days: localCalc.breakdown.days,
        daily_rate: localCalc.raw.rental_amount / localCalc.breakdown.days,
        number_of_days: localCalc.breakdown.days,
        commission_rate_percent: commissionRatePercent,
        vat_rate_percent: vatRatePercent
      };
    }
    
    return null;
  };

  // Gérer le succès du paiement Stripe
  const handleStripePaymentSuccess = async (completeReservationData) => {
    console.log('✅ [ReservationSidebar] Complete reservation with payment created:', completeReservationData);
    
    try {
      showNotification(t('sidebar.notifications.successWithPayment'), 'success');
      
      // Rediriger vers la page de confirmation avec l'ID de la réservation
      const reservationId = completeReservationData.reservation?.id;
      if (reservationId) {
        // Fermer la sidebar d'abord
        onClose();
        
        // Rediriger vers la page de confirmation
        navigate(`/booking/confirmation/${reservationId}`);
      } else {
        console.error('❌ [ReservationSidebar] No reservation ID found in response');
        showNotification(t('sidebar.notifications.reservationIdMissing'), 'error');
        setReservationStep('confirmation');
        
        // Sauvegarder toutes les données de la réservation complète en fallback
        setReservationCreated({
          reservation: completeReservationData.reservation,
          payment: completeReservationData.payment,
          calculation: completeReservationData.calculation,
          codes: completeReservationData.codes,
          documents: completeReservationData.documents
        });
      }
      
    } catch (error) {
      console.error('❌ [ReservationSidebar] Error handling payment success:', error);
      showNotification(t('sidebar.notifications.finalizationError'), 'error');
    }
  };

  // Gérer l'erreur du paiement Stripe
  const handleStripePaymentError = (error) => {
    console.error('❌ [ReservationSidebar] Stripe payment error:', error);
    
    setPaymentError(error);
    setShowPaymentError(true);
    setReservationStep('payment'); // Retour au choix du moyen de paiement
  };

  // Annuler le paiement Stripe
  const handleStripePaymentCancel = () => {
    console.log('↩️ [ReservationSidebar] Stripe payment cancelled');
    setReservationStep('payment'); // Retour au choix du moyen de paiement
    setStripeData(null);
  };

  // Gérer les actions du modal d'erreur de paiement
  const handlePaymentErrorRetry = () => {
    console.log('🔄 [ReservationSidebar] Retrying payment');
    setShowPaymentError(false);
    setReservationStep('stripe_payment'); // Revenir au paiement
  };

  const handlePaymentErrorChangeMethod = () => {
    console.log('💳 [ReservationSidebar] Changing payment method');
    setShowPaymentError(false);
    setPaymentMethod(null);
    setReservationStep('payment'); // Retour au choix de méthode de paiement
  };

  const handlePaymentErrorContactSupport = () => {
    console.log('📞 [ReservationSidebar] Contacting support');
    setShowPaymentError(false);
    // Ici on pourrait ouvrir un système de chat ou rediriger vers support
    window.open('mailto:support@krillou.com?subject=Problème de paiement&body=Bonjour, je rencontre un problème lors du paiement de ma réservation.', '_blank');
  };

  const handleClosePaymentError = () => {
    setShowPaymentError(false);
    setPaymentError(null);
  };

  // Passer à l'étape du formulaire
  const proceedToForm = () => {
    if (!startDate || !endDate) {
      showNotification(t('sidebar.notifications.selectDates'), 'warning');
      return;
    }

    // Validation supplémentaire pour les créneaux horaires
    if (isTimeSlotMode && !slotSelectionValid) {
      showNotification(slotSelectionReason || 'Sélection de créneaux invalide', 'warning');
      return;
    }
    
    if (!isTimeSlotMode && !localPriceCalculation) {
      showNotification(t('sidebar.notifications.calculating'), 'info');
      return;
    }
    
    // Si le calcul backend n'est pas encore terminé, on attend un peu
    if (calculatingPrice) {
      showNotification(t('sidebar.notifications.finalization'), 'info');
      return;
    }
    
    // Si on n'a pas encore le calcul backend, on essaie de le déclencher
    if (!priceCalculation) {
      calculatePrice(startDate, endDate);
      showNotification(t('sidebar.notifications.preparation'), 'info');
      return;
    }
    
    setReservationStep('form');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', md: 450 },
          maxWidth: '100vw',
          height: '100%',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* En-tête */}
        <Box
          sx={{
            p: 1.5,
            background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {t('sidebar.title')} - {listing?.title}
          </Typography>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{ 
              color: 'white',
              background: 'rgba(255,255,255,0.1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contenu dynamique selon l'étape */}
        <Box sx={{ flex: 1, px: 2, py: 1, overflow: 'auto' }}>

          {/* Paramètres de réservation (accordéon rétractable) */}
          <BookingSettingsSummary listing={listing} variant="accordion" defaultExpanded={false} />
          
          {/* Étape 1: Calendrier — kept mounted to preserve SlotPicker/Calendar state on back navigation */}
          <Box sx={{ display: reservationStep === 'calendar' ? 'block' : 'none' }}>
              {!isTimeSlotMode ? (
                <AvailabilityCalendar
                  listingId={listing?.id}
                  listing={listing}
                  onDateRangeSelect={handleDateChange}
                  onDateRangeChange={handleDateChange}
                  selectedStartDate={startDate}
                  selectedEndDate={endDate}
                />
              ) : (
                <SlotPicker
                  listing={listing}
                  onSelectionChange={handleSlotSelectionChange}
                  quantity_requested={formData.quantity_requested}
                  seats_requested={formData.seats_requested}
                />
              )}
              
              {/* Résumé des dates + calcul prix */}
              {startDate && endDate && (
                <Card sx={{ mt: 1.5, background: 'rgba(255,255,255,0.9)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      {t('sidebar.calendar.selectedDates')}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{isNightsMode ? t('sidebar.calendar.checkIn', 'Arrivée') : t('sidebar.calendar.from')} :</strong>{' '}
                        {format(startDate, isTimeSlotMode ? 'dd MMMM yyyy HH:mm' : 'dd MMMM yyyy', { locale: dateLocale })}
                        {isDateRangeMode && <span style={{ color: '#6200ee', fontWeight: 600 }}> — {listing?.booking_settings?.checkin_time || defaultCheckinTime}</span>}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{isNightsMode ? t('sidebar.calendar.checkOut', 'Départ') : t('sidebar.calendar.to')} :</strong>{' '}
                        {format(endDate, isTimeSlotMode ? 'dd MMMM yyyy HH:mm' : 'dd MMMM yyyy', { locale: dateLocale })}
                        {isDateRangeMode && <span style={{ color: '#6200ee', fontWeight: 600 }}> — {listing?.booking_settings?.checkout_time || defaultCheckoutTime}</span>}
                      </Typography>
                      {!isTimeSlotMode && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('sidebar.calendar.duration')} :</strong> {computeDateRangeDuration(startDate, endDate)} {durationUnitLabel}
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Calcul du prix */}
                    {calculatingPrice && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">{t('sidebar.pricing.calculating')}</Typography>
                      </Box>
                    )}

                    {displayData && !calculatingPrice && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                          {t('sidebar.pricing.title')}
                        </Typography>
                        
                        {!isTimeSlotMode && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {t('sidebar.pricing.dailyRate', {
                                price: formatListingPrice(displayData.daily_rate),
                                days: displayData.number_of_days
                              })}
                            </Typography>
                            <Typography variant="body2">
                              {formatListingPrice(displayData.rental_amount)}
                            </Typography>
                          </Box>
                        )}

                        {isTimeSlotMode && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {t('sidebar.pricing.title')}
                            </Typography>
                            <Typography variant="body2">
                              {formatListingPrice(displayData.rental_amount)}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {t('sidebar.pricing.serviceFee', { percent: displayData.commission_rate_percent ?? PRICING_CONFIG.SERVICE_FEE_PERCENT })}
                          </Typography>
                          <Typography variant="body2">
                            {formatListingPrice(displayData.service_fee)}
                          </Typography>
                        </Box>
                        
                        {displayData.tax_amount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">{t('sidebar.pricing.tax', { percent: displayData.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT })}</Typography>
                            <Typography variant="body2">
                              {formatListingPrice(displayData.tax_amount)}
                            </Typography>
                          </Box>
                        )}
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('sidebar.pricing.total')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatListingPrice(displayData.total_amount)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('sidebar.pricing.deposit')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {(displayData.deposit_amount ?? 0) > 0 
                              ? formatListingPrice(displayData.deposit_amount)
                              : t('sidebar.payment.noDeposit', 'Aucune caution requise')
                            }
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {priceError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {t(priceError, { defaultValue: priceError })}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={proceedToForm}
                      disabled={
                        !startDate || 
                        !endDate || 
                        !!priceError ||
                        (!isTimeSlotMode && !localPriceCalculation) ||
                        (isTimeSlotMode && !slotSelectionValid) ||
                        (listing?.stock_quantity > 1 && availableUnits <= 0) ||
                        (listing?.booking_mode === 'time_slot_capacity' && availableCapacity != null && availableCapacity <= 0)
                      }
                      sx={{
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                          }
                        }}
                    >
                      {calculatingPrice ? t('sidebar.buttons.calculating') : t('sidebar.buttons.continue')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {(!startDate || !endDate) && (
                <Box sx={{ textAlign: 'center', py: 3, mt: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('sidebar.calendar.selectDates')}
                  </Typography>
                </Box>
              )}
          </Box>

          {/* Étape 2: Formulaire de réservation */}
          {reservationStep === 'form' && (
            <>
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="text"
                  onClick={() => setReservationStep('calendar')}
                  sx={{ mb: 2 }}
                >
                  {t('sidebar.form.backToCalendar')}
                </Button>
                
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  {t('sidebar.form.title')}
                </Typography>
              </Box>

              <Card sx={{ background: 'rgba(255,255,255,0.9)', mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('sidebar.form.summary')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('sidebar.form.dates')}:</strong>{' '}
                      {isTimeSlotMode
                      ? `${format(startDate, 'dd/MM/yyyy HH:mm', { locale: dateLocale })} → ${format(endDate, 'HH:mm', { locale: dateLocale })}  (${t('sidebar.form.slotCount', { count: selectedSlots.length })})`
                      : isDateRangeMode
                        ? `${format(startDate, 'dd/MM/yyyy', { locale: dateLocale })} ${listing?.booking_settings?.checkin_time || defaultCheckinTime} → ${format(endDate, 'dd/MM/yyyy', { locale: dateLocale })} ${listing?.booking_settings?.checkout_time || defaultCheckoutTime}`
                        : `${format(startDate, 'dd/MM/yyyy', { locale: dateLocale })} - ${format(endDate, 'dd/MM/yyyy', { locale: dateLocale })}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('sidebar.form.total')}:</strong> {formatListingPrice(priceCalculation?.total_amount || localPriceCalculation?.breakdown?.total_amount || displayData?.total_amount)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('sidebar.form.deposit')}:</strong> {((priceCalculation?.deposit_amount ?? localPriceCalculation?.breakdown?.deposit_amount ?? displayData?.deposit_amount) ?? 0) > 0 
                      ? formatListingPrice(priceCalculation?.deposit_amount || localPriceCalculation?.breakdown?.deposit_amount || displayData?.deposit_amount)
                      : t('sidebar.payment.noDeposit', 'Aucune caution requise')
                    }
                  </Typography>
                </CardContent>
              </Card>

              {/* Formulaire */}
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Adresse du lieu (pré-remplie, lecture seule) */}
                {listing?.address && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Lieu de la réservation
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {listing.address}
                        </Typography>
                        {listing.city && (
                          <Typography variant="body2" color="text.secondary">
                            {listing.city}{listing.postalCode ? `, ${listing.postalCode}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {listing.latitude && listing.longitude && (
                      <ReadOnlyMap
                        latitude={listing.latitude}
                        longitude={listing.longitude}
                        address={listing.address}
                        height={140}
                        zoom={15}
                        showAddress={false}
                      />
                    )}
                  </Box>
                )}

                {/* ── Contrôles dynamiques selon pricing_mode ─────────────────────── */}
                {/* Règle physique (per_unit_per_seat) :
                     - seats_requested = total passagers
                     - quantity_requested = ceil(seats / capacityPerUnit), dérivé automatiquement
                     - Si seats ≤ capacityPerUnit → uniForcée=1, champ désactivé
                */}
                {(() => {
                  const pm = listing?.booking_settings?.pricing_mode;
                  const capacityPerUnit = listing?.capacity_default || listing?.booking_settings?.capacity_per_unit || 1;
                  const stockMax = availableUnits || listing?.stock_quantity || 1;
                  const showUnits = hasMultipleUnits && (pm === 'per_unit' || pm === 'per_unit_per_seat');
                  const showSeats = (pm === 'per_seat' || pm === 'per_unit_per_seat') && listing?.booking_mode === 'time_slot_capacity';

                  // per_unit_per_seat : les places (passagers totaux) pilotent les unités
                  const minUnitsForSeats = pm === 'per_unit_per_seat'
                    ? Math.max(1, Math.ceil(formData.seats_requested / capacityPerUnit))
                    : 1;
                  const unitsLockedToOne = pm === 'per_unit_per_seat' && formData.seats_requested <= capacityPerUnit;

                  const handleSeatsChange = (rawVal) => {
                    const maxSeats = pm === 'per_unit_per_seat'
                      ? capacityPerUnit * stockMax
                      : availableCapacity != null ? availableCapacity : (capacityPerUnit * stockMax);
                    const val = Math.min(maxSeats, Math.max(1, parseInt(rawVal) || 1));
                    handleFormChange('seats_requested', val);
                    // Dériver automatiquement le nombre d'unités nécessaires
                    if (pm === 'per_unit_per_seat') {
                      const derivedQty = Math.max(1, Math.ceil(val / capacityPerUnit));
                      handleFormChange('quantity_requested', derivedQty);
                    }
                  };

                  return (<>
                    {/* Places EN PREMIER en per_unit_per_seat : c'est la donnée primaire */}
                    {showSeats && (
                      <TextField
                        label={pm === 'per_unit_per_seat'
                          ? t('sidebar.form.totalPassengersLabel', 'Nombre total de passagers')
                          : t('sidebar.form.seatsLabel', 'Places à réserver')}
                        type="number"
                        fullWidth
                        value={formData.seats_requested}
                        onChange={(e) => handleSeatsChange(e.target.value)}
                        inputProps={{
                          min: 1,
                          max: pm === 'per_unit_per_seat'
                            ? capacityPerUnit * stockMax
                            : availableCapacity != null ? availableCapacity : (capacityPerUnit || 99)
                        }}
                        helperText={
                          availableCapacity != null && availableCapacity <= 0
                            ? t('sidebar.form.seats.helper.noAvailability', 'Aucune place disponible sur ce créneau')
                            : pm === 'per_unit_per_seat'
                              ? t('sidebar.form.seats.helper.capacitySummary', { capacity: capacityPerUnit, seats: formData.seats_requested, minUnits: minUnitsForSeats })
                              : availableCapacity != null
                                ? t('sidebar.form.seats.helper.availablePriceNote', { count: availableCapacity })
                                : t('sidebar.form.seats.helper.maxPerUnit', { capacity: capacityPerUnit })
                        }
                        error={availableCapacity != null && availableCapacity <= 0}
                        disabled={availableCapacity != null && availableCapacity <= 0}
                      />
                    )}
                    {/* Unités EN SECOND : dérivées des places en per_unit_per_seat */}
                    {showUnits && (
                      <TextField
                        label={t('sidebar.form.unitsLabel', 'Unités à réserver')}
                        type="number"
                        fullWidth
                        value={formData.quantity_requested}
                        onChange={(e) => {
                          if (unitsLockedToOne) return; // contrôlé par les places
                          const val = Math.min(stockMax, Math.max(minUnitsForSeats, parseInt(e.target.value) || 1));
                          handleFormChange('quantity_requested', val);
                        }}
                        inputProps={{ min: minUnitsForSeats, max: stockMax }}
                        helperText={
                          availableUnits <= 0
                            ? t('sidebar.form.units.helper.noAvailability', 'Aucune unité disponible sur ces dates')
                            : unitsLockedToOne
                              ? t('sidebar.form.units.helper.lockedToOne', { seats: formData.seats_requested, capacity: capacityPerUnit })
                              : pm === 'per_unit_per_seat'
                                ? t('sidebar.form.units.helper.minUnitsForSeats', { minUnits: minUnitsForSeats, seats: formData.seats_requested, stockMax })
                                : t('sidebar.form.units.helper.stockAvailable', { stockMax })
                        }
                        error={availableUnits <= 0}
                        disabled={availableUnits <= 0 || unitsLockedToOne}
                        sx={unitsLockedToOne ? { opacity: 0.7 } : {}}
                      />
                    )}
                  </>);
                })()}

                {priceError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {t(priceError, { defaultValue: priceError })}
                  </Alert>
                )}

                <TextField
                  label={t('sidebar.form.specialRequests.label')}
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.special_requests}
                  onChange={(e) => handleFormChange('special_requests', e.target.value)}
                  helperText={t('sidebar.form.specialRequests.helperText')}
                />

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => setReservationStep('payment')}
                  disabled={!!priceError}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                    }
                  }}
                >
                  {t('sidebar.buttons.choosePayment')}
                </Button>
              </Box>
            </>
          )}

          {/* Étape 3: Choix du moyen de paiement */}
          {reservationStep === 'payment' && (
            <>
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="text"
                  onClick={() => setReservationStep('form')}
                  sx={{ mb: 2 }}
                >
                  {t('sidebar.payment.backToForm')}
                </Button>
                
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  {t('sidebar.payment.title')}
                </Typography>
              </Box>

              {/* Récapitulatif de la réservation */}
              <Card sx={{ background: 'rgba(255,255,255,0.9)', mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('sidebar.payment.finalSummary')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('sidebar.form.dates')}:</strong>{' '}
                    {isTimeSlotMode
                      ? `${format(startDate, 'dd/MM/yyyy HH:mm', { locale: dateLocale })} → ${format(endDate, 'HH:mm', { locale: dateLocale })}  (${t('sidebar.form.slotCount', { count: selectedSlots.length })})`
                      : isDateRangeMode
                        ? `${format(startDate, 'dd/MM/yyyy', { locale: dateLocale })} ${listing?.booking_settings?.checkin_time || defaultCheckinTime} → ${format(endDate, 'dd/MM/yyyy', { locale: dateLocale })} ${listing?.booking_settings?.checkout_time || defaultCheckoutTime}`
                        : `${format(startDate, 'dd/MM/yyyy', { locale: dateLocale })} - ${format(endDate, 'dd/MM/yyyy', { locale: dateLocale })}`}
                  </Typography>
                  {/* Unités + Places avec contexte physique clair */}
                  {(() => {
                    const pm = listing?.booking_settings?.pricing_mode;
                    const capacityPerUnit = listing?.capacity_default || listing?.booking_settings?.capacity_per_unit || 1;
                    const qty = formData.quantity_requested;
                    const seats = formData.seats_requested;
                    if (pm === 'per_unit_per_seat' && listing?.booking_mode === 'time_slot_capacity') {
                      return (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t('sidebar.form.passengersLabel', 'Passagers')} :</strong> {seats} {seats > 1 ? t('sidebar.form.passengers_plural', 'passagers') : t('sidebar.form.passenger', 'passager')}
                          {' '}{t('sidebar.form.in', 'dans')} {qty} {qty > 1 ? t('sidebar.form.units_plural', 'unités') : t('sidebar.form.unit', 'unité')}
                          {' '}( {t('sidebar.form.capacityPerUnit', 'capacité')} {capacityPerUnit}/{t('sidebar.form.unit', 'unité')}{qty > 1 ? ` = ${qty * capacityPerUnit} ${t('sidebar.form.placesAvailable', 'places dispo')}` : ''})
                        </Typography>
                      );
                    }
                    return (<>
                      {hasMultipleUnits && ['per_unit'].includes(pm) && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t('sidebar.form.unitsLabel', 'Unités')} :</strong> {qty} {qty > 1 ? t('sidebar.form.units_plural', 'unités') : t('sidebar.form.unit', 'unité')}
                        </Typography>
                      )}
                      {['per_seat'].includes(pm) && listing?.booking_mode === 'time_slot_capacity' && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t('sidebar.form.placesLabel', 'Places')} :</strong> {seats} {seats > 1 ? t('sidebar.form.places_plural', 'places') : t('sidebar.form.place', 'place')}
                        </Typography>
                      )}
                    </>);
                  })()}

                  {/* Détail du prix (aligné avec modal Stripe) */}
                  <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1, mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('common:rental', 'Location')} {displayData?.days === 0 || !displayData?.days ? 1 : displayData?.days} {t('settings.slotPicker.unitDay', 'jour(s)')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatListingPrice(priceCalculation?.rental_amount || priceCalculation?.base_amount || localPriceCalculation?.breakdown?.rental_amount || displayData?.rental_amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('common:serviceFee', 'Frais de service')} ({displayData?.commission_rate_percent ?? PRICING_CONFIG.SERVICE_FEE_PERCENT}%)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatListingPrice(priceCalculation?.service_fee || localPriceCalculation?.breakdown?.service_fee || displayData?.service_fee)}
                      </Typography>
                    </Box>
                    {(displayData?.tax_amount > 0) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {(() => { const vr = displayData?.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT; return t('sidebar.pricing.tax', { percent: vr, defaultValue: `dont TVA (${vr}%)` }); })()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {formatListingPrice(displayData.tax_amount)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2"><strong>{t('common:subtotal', 'Sous-total')}</strong></Typography>
                      <Typography variant="body2"><strong>{formatListingPrice(priceCalculation?.total_amount || localPriceCalculation?.breakdown?.total_amount || displayData?.total_amount)}</strong></Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {((priceCalculation?.deposit_amount ?? localPriceCalculation?.breakdown?.deposit_amount ?? displayData?.deposit_amount) ?? 0) > 0 
                          ? `${t('sidebar.form.deposit')} (${t('sidebar.payment.depositRefundable', 'remboursable')})`
                          : t('sidebar.form.deposit')
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {((priceCalculation?.deposit_amount ?? localPriceCalculation?.breakdown?.deposit_amount ?? displayData?.deposit_amount) ?? 0) > 0 
                          ? formatListingPrice(priceCalculation?.deposit_amount || localPriceCalculation?.breakdown?.deposit_amount || displayData?.deposit_amount)
                          : t('sidebar.payment.noDeposit', 'Aucune caution requise')
                        }
                      </Typography>
                    </Box>
                  </Box>

                  {/* Lieu */}
                  {listing?.address && (
                    <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1, mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Lieu :</strong> {listing.address}
                      </Typography>
                    </Box>
                  )}

                  {/* Note remboursement caution (aligné avec modal Stripe) */}
                  {(priceCalculation?.deposit_amount || localPriceCalculation?.breakdown?.deposit_amount || displayData?.deposit_amount) > 0 && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f0f8ff', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        💡 {t('sidebar.payment.depositRefundNotice', "La caution sera intégralement remboursée à l'issue de la prestation, en l'absence de dommage constaté.")}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Options de paiement */}
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                {t('sidebar.payment.howToPay')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                
                {/* Option 1: Espèces à la livraison */}
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: paymentMethod === 'cash' ? '2px solid #6200ee' : '1px solid #e0e0e0',
                    '&:hover': { borderColor: '#6200ee' }
                  }}
                  onClick={() => setPaymentMethod('cash')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '50%', 
                        border: '2px solid',
                        borderColor: paymentMethod === 'cash' ? '#6200ee' : '#ccc',
                        backgroundColor: paymentMethod === 'cash' ? '#6200ee' : 'transparent'
                      }} />
                      <Box>
                        <Typography variant="subtitle2">{t('sidebar.payment.methods.cash.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sidebar.payment.methods.cash.description')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Option 2: Virement vers la société (DÉSACTIVÉ) */}
                <Card 
                  sx={{ 
                    cursor: 'not-allowed',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#f5f5f5',
                    opacity: 0.6,
                    position: 'relative'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '50%', 
                        border: '2px solid #ccc',
                        backgroundColor: 'transparent'
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{t('sidebar.payment.methods.bankTransfer.title')}</Typography>
                          <Chip 
                            label={t('sidebar.payment.methods.comingSoon', 'Bientôt disponible')} 
                            size="small" 
                            color="warning" 
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('sidebar.payment.methods.bankTransfer.description')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Option 3: Paiement par carte */}
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: paymentMethod === 'card' ? '2px solid #6200ee' : '1px solid #e0e0e0',
                    '&:hover': { borderColor: '#6200ee' }
                  }}
                  onClick={() => setPaymentMethod('card')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '50%', 
                        border: '2px solid',
                        borderColor: paymentMethod === 'card' ? '#6200ee' : '#ccc',
                        backgroundColor: paymentMethod === 'card' ? '#6200ee' : 'transparent'
                      }} />
                      <Box>
                        <Typography variant="subtitle2">{t('sidebar.payment.methods.card.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sidebar.payment.methods.card.description')}
                        </Typography>
                        <Chip label={t('sidebar.payment.methods.card.secure')} size="small" color="success" sx={{ mt: 1 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

              </Box>

              {/* Bouton de confirmation */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleReservation}
                disabled={!paymentMethod || loading || !!priceError}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #6200ee 30%, #4b00b5 90%)',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <Typography>{t('sidebar.buttons.creating')}</Typography>
                  </Box>
                ) : (
                  `${t('sidebar.buttons.confirm')} (${formatListingPrice(priceCalculation?.total_amount || localPriceCalculation?.breakdown?.total_amount || displayData?.total_amount)})`
                )}
              </Button>
            </>
          )}

          {/* Étape 3.5: Paiement Stripe avec Saisie Carte */}
          {reservationStep === 'stripe_payment' && stripeData && stripeData.reservationData && (
            <StripePaymentModal
              reservationData={stripeData.reservationData}
              breakdown={stripeData.breakdown}
              onSuccess={handleStripePaymentSuccess}
              onError={handleStripePaymentError}
              onCancel={handleStripePaymentCancel}
              onProcessingChange={setStripeProcessing}
            />
          )}

          {/* Modal de confirmation pour paiements cash / virement bancaire */}
          <CashConfirmationModal
            open={showCashModal}
            onClose={() => { setShowCashModal(false); setCashModalData(null); }}
            onConfirm={handleCashConfirmed}
            reservationData={cashModalData?.reservationData}
            breakdown={cashModalData?.breakdown}
            paymentMethod={paymentMethod}
            loading={loading}
          />

          {/* Étape 4: Confirmation */}
          {reservationStep === 'confirmation' && (
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" sx={{ color: 'success.main', mb: 2, textAlign: 'center' }}>
                {t('sidebar.confirmation.title')}
              </Typography>
              
              {reservationCreated && (
                <Box sx={{ mb: 3 }}>
                  {/* Informations de la réservation */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{t('sidebar.confirmation.details')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('sidebar.confirmation.id')}:</strong> {reservationCreated.reservation?.id}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('sidebar.confirmation.dates')}:</strong>{' '}
                      {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
                      {isDateRangeMode && (
                        <span style={{ color: '#6200ee', fontWeight: 600 }}>
                          {' '}({listing?.booking_settings?.checkin_time || defaultCheckinTime} → {listing?.booking_settings?.checkout_time || defaultCheckoutTime})
                        </span>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('sidebar.confirmation.status')}:</strong> {reservationCreated.reservation?.status}
                    </Typography>
                  </Box>

                  {/* Informations de paiement */}
                  {reservationCreated.payment && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>{t('sidebar.confirmation.payment')}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('sidebar.confirmation.totalPaid')}:</strong> {reservationCreated.payment.total_charged}€
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('sidebar.confirmation.paymentId')}:</strong> {reservationCreated.payment.rental_payment_id}
                      </Typography>
                      {reservationCreated.payment.deposit_id && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t('sidebar.confirmation.depositId')}:</strong> {reservationCreated.payment.deposit_id}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Codes QR */}
                  {reservationCreated.codes && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>{t('sidebar.confirmation.qrCodes')}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('sidebar.confirmation.pickupCode')}:</strong> {reservationCreated.codes.pickup?.id}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('sidebar.confirmation.returnCode')}:</strong> {reservationCreated.codes.return?.id}
                      </Typography>
                    </Box>
                  )}

                  {/* Documents */}
                  {reservationCreated.documents && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>{t('sidebar.confirmation.documents')}</Typography>
                      {reservationCreated.documents.contract && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t('sidebar.confirmation.contract')}:</strong> {reservationCreated.documents.contract.id}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                    {t('sidebar.confirmation.emailInfo')}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Rediriger vers la page des réservations
                    window.location.href = '/mes-reservations';
                  }}
                >
                  {t('sidebar.confirmation.actions.viewReservations')}
                </Button>
                <Button
                  variant="contained"
                  onClick={onClose}
                >
                  {t('sidebar.confirmation.actions.close')}
                </Button>
              </Box>
            </Box>
          )}
          
        </Box>
      </Box>

      {/* Overlay de chargement – cash/virement ET soumission carte Stripe */}
      <LoadingOverlay
        isOpen={loading || stripeProcessing}
        title={t('sidebar.loading.processingTitle', 'Traitement en cours...')}
        subtitle={t('sidebar.loading.processingSubtitle', 'Votre réservation est en cours de traitement. Veuillez patienter...')}
        warning={t('sidebar.loading.doNotClose', 'Ne fermez pas cette page.')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
      />

      {/* Modal d'erreur de paiement */}
      <PaymentErrorModal
        isOpen={showPaymentError}
        error={paymentError}
        onClose={handleClosePaymentError}
        onRetry={handlePaymentErrorRetry}
        onChangePaymentMethod={handlePaymentErrorChangeMethod}
        onContactSupport={handlePaymentErrorContactSupport}
        reservationAmount={
          priceCalculation && priceCalculation.total_amount
            ? formatUnifiedPrice(priceCalculation.total_amount, priceCalculation.currency || 'EUR')
            : null
        }
      />
    </Drawer>
  );
};

export default ReservationSidebar;
